import { Download01 } from "@untitledui/icons";
import { useMemo, useState } from "react";
import { Table, TableCard } from "@/components/application/table/table";
import { Badge } from "@/components/base/badges/badges";
import { Button } from "@/components/base/buttons/button";
import { Checkbox } from "@/components/base/checkbox/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import type { FlagQaRecord, MerchantRiskRow, RiskDimension, RiskProblem } from "../flag-qa-types";
import { QUALITY_BAND_COLORS, RISK_PROBLEM_COLORS, RISK_PROBLEM_LABELS } from "../flag-qa-types";
import { actionToOutcome, buildFlagDetail, buildMerchantRiskSummary, getQualityBand, slugify } from "../flag-qa-utils";

const RISK_DIMENSIONS: { value: RiskDimension; label: string }[] = [
  { value: "newFlag", label: "New Flag (matches Category column)" },
  { value: "riskTypeShort", label: "Risk Type" },
];

const pairKey = (row: { merchantId: string; risk: string }) => `${row.merchantId} ${row.risk}`;

const exportAtRiskToCSV = (rows: MerchantRiskRow[], filename: string) => {
  const headers = [
    "Merchant",
    "Risk",
    "Problem",
    "Total Flags",
    "FP (flag_dismissed)",
    "TP (flag_is_correct)",
    "FN (add_flag)",
    "Precision (%)",
    "Recall (%)",
    "F1 (%)",
  ];

  const csvRows = rows.map((row) => [
    row.merchantId,
    row.risk,
    row.problem ? RISK_PROBLEM_LABELS[row.problem] : "",
    row.total,
    row.fp,
    row.tp,
    row.fn,
    row.precision,
    row.recall,
    row.f1,
  ]);

  downloadCSV([headers.join(","), ...csvRows.map((row) => row.join(","))].join("\n"), filename);
};

const exportFlagDetailToCSV = (rows: FlagQaRecord[], filename: string) => {
  const headers = [
    "ID",
    "Merchant ID",
    "Video ID",
    "Risk Type",
    "Original Flag",
    "New Flag",
    "Outcome",
    "Action Taken",
    "Confidence Index",
    "Explanation",
    "Visual Trigger",
    "Transcript Trigger",
    "Feedback Reason",
    "Feedback Text",
    "Flag Created At",
    "Edit Created At",
  ];

  const quote = (value: string | null) => `"${(value || "").replace(/"/g, '""')}"`;

  const csvRows = rows.map((record) => [
    record.id,
    record.merchantId,
    record.videoId.replace("VID-", ""),
    record.riskTypeShort,
    record.originalFlag || "",
    record.newFlag || "",
    actionToOutcome(record.actionTaken) || "",
    record.actionTaken || "",
    record.confidenceIndex,
    quote(record.explanation),
    quote(record.visualEvidence),
    quote(record.transcriptEvidence),
    record.feedbackReason || "",
    quote(record.feedbackText),
    record.flagCreatedAt,
    record.editCreatedAt || "",
  ]);

  downloadCSV([headers.join(","), ...csvRows.map((row) => row.join(","))].join("\n"), filename);
};

const downloadCSV = (csv: string, filename: string) => {
  const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
  const link = document.createElement("a");
  const url = URL.createObjectURL(blob);
  link.setAttribute("href", url);
  link.setAttribute("download", filename);
  link.style.visibility = "hidden";
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
};

function F1Cell({ f1 }: { f1: number }) {
  return (
    <span className="font-semibold" style={{ color: QUALITY_BAND_COLORS[getQualityBand(f1)] }}>
      {f1.toFixed(1)}%
    </span>
  );
}

function ProblemCell({ problem }: { problem: RiskProblem | null }) {
  if (!problem) return <span className="text-tertiary">—</span>;
  return (
    <span className="font-semibold" style={{ color: RISK_PROBLEM_COLORS[problem] }}>
      {RISK_PROBLEM_LABELS[problem]}
    </span>
  );
}

function OutcomeBadge({ actionTaken }: { actionTaken: string | null }) {
  const outcome = actionToOutcome(actionTaken);
  if (!outcome) return <span className="text-tertiary">—</span>;
  return (
    <Badge color={outcome === "TP" ? "success" : outcome === "FP" ? "orange" : "error"} size="sm">
      {outcome}
    </Badge>
  );
}

interface FlagQaRiskAttentionProps {
  data: FlagQaRecord[];
}

export function FlagQaRiskAttention({ data }: FlagQaRiskAttentionProps) {
  const [dimension, setDimension] = useState<RiskDimension>("newFlag");
  const [f1Threshold, setF1Threshold] = useState(70);
  const [minFlags, setMinFlags] = useState(20);
  const [showAllPairs, setShowAllPairs] = useState(false);
  const [selectedPairKey, setSelectedPairKey] = useState<string | null>(null);
  const [includeUnreviewed, setIncludeUnreviewed] = useState(false);

  const full = useMemo(() => buildMerchantRiskSummary(data, dimension), [data, dimension]);

  const atRisk = useMemo(
    () => full.filter((row) => row.total >= minFlags && row.f1 < f1Threshold),
    [full, minFlags, f1Threshold],
  );

  const excludedLowVolume = useMemo(
    () => full.filter((row) => row.total < minFlags && row.f1 < f1Threshold).length,
    [full, minFlags, f1Threshold],
  );

  const problemCounts = useMemo(() => {
    const counts: Record<RiskProblem, number> = { falsePositives: 0, falseNegatives: 0, mixed: 0 };
    for (const row of atRisk) {
      if (row.problem) counts[row.problem] += 1;
    }
    return counts;
  }, [atRisk]);

  const merchantsAtRisk = new Set(atRisk.map((row) => row.merchantId)).size;
  const flagsAffected = atRisk.reduce((sum, row) => sum + row.total, 0);

  const selectedPair = useMemo(
    () => (selectedPairKey ? (atRisk.find((row) => pairKey(row) === selectedPairKey) ?? null) : null),
    [atRisk, selectedPairKey],
  );

  const flagDetail = useMemo(
    () =>
      selectedPair
        ? buildFlagDetail(data, selectedPair.merchantId, selectedPair.risk, dimension, !includeUnreviewed)
        : [],
    [data, selectedPair, dimension, includeUnreviewed],
  );

  if (full.length === 0) {
    return (
      <div className="text-center py-8 text-tertiary">
        No reviewed flags (TP/FP/FN) in this selection, so there is nothing to score yet.
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-6">
      {/* Controls */}
      <TableCard.Root className="p-4">
        <div className="flex gap-6 items-end">
          <div className="flex-1 max-w-md">
            <label className="text-sm font-medium mb-2 block text-secondary">Risk Dimension</label>
            <Select value={dimension} onValueChange={(v) => setDimension(v as RiskDimension)}>
              <SelectTrigger className="bg-gray-800 border-gray-700 text-primary">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-gray-800 border-gray-700 text-white">
                {RISK_DIMENSIONS.map((d) => (
                  <SelectItem key={d.value} value={d.value} className="text-white focus:bg-gray-700">
                    {d.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="flex-1">
            <label className="text-sm font-medium mb-2 block text-secondary">At risk below F1 (%)</label>
            <div className="flex items-center gap-3">
              <input
                type="range"
                min={0}
                max={100}
                step={5}
                value={f1Threshold}
                onChange={(e) => setF1Threshold(Number(e.target.value))}
                className="flex-1"
              />
              <span className="text-sm text-primary w-12 text-right">{f1Threshold}%</span>
            </div>
          </div>

          <div className="flex-1">
            <label className="text-sm font-medium mb-2 block text-secondary">Min. flags to qualify</label>
            <div className="flex items-center gap-3">
              <input
                type="range"
                min={1}
                max={200}
                value={minFlags}
                onChange={(e) => setMinFlags(Number(e.target.value))}
                className="flex-1"
              />
              <span className="text-sm text-primary w-12 text-right">{minFlags}</span>
            </div>
          </div>
        </div>
      </TableCard.Root>

      {/* KPI row */}
      <div className="grid grid-cols-4 gap-4">
        <TableCard.Root className="p-4">
          <div className="text-sm text-tertiary mb-1">Merchants at Risk</div>
          <div className="text-3xl font-bold text-primary">{merchantsAtRisk}</div>
        </TableCard.Root>
        <TableCard.Root className="p-4">
          <div className="text-sm text-tertiary mb-1">Merchant × Risk Pairs</div>
          <div className="text-3xl font-bold text-primary">{atRisk.length}</div>
        </TableCard.Root>
        <TableCard.Root className="p-4">
          <div className="text-sm text-tertiary mb-1">Flags Affected</div>
          <div className="text-3xl font-bold text-primary">{flagsAffected}</div>
        </TableCard.Root>
        <TableCard.Root className="p-4">
          <div className="text-sm text-tertiary mb-1">Excluded (Low Volume)</div>
          <div className="text-3xl font-bold text-yellow-500">{excludedLowVolume}</div>
          <div className="text-xs text-tertiary mt-1">below threshold but under min. flags</div>
        </TableCard.Root>
      </div>

      {/* Problem legend */}
      <div className="flex flex-wrap gap-x-6 gap-y-1 text-sm">
        <span>
          <span className="font-bold" style={{ color: RISK_PROBLEM_COLORS.falsePositives }}>
            ■ False positives ({problemCounts.falsePositives})
          </span>
          <span className="text-secondary"> — over-flagging, clean content getting dismissed</span>
        </span>
        <span>
          <span className="font-bold" style={{ color: RISK_PROBLEM_COLORS.falseNegatives }}>
            ■ False negatives ({problemCounts.falseNegatives})
          </span>
          <span className="text-secondary"> — misses, reviewers adding flags we didn't raise</span>
        </span>
        <span>
          <span className="font-bold" style={{ color: RISK_PROBLEM_COLORS.mixed }}>
            ■ Mixed ({problemCounts.mixed})
          </span>
          <span className="text-secondary"> — both</span>
        </span>
      </div>

      {atRisk.length === 0 ? (
        <div className="text-center py-8 text-tertiary">
          Nothing below {f1Threshold}% F1 with at least {minFlags} flags. {excludedLowVolume} low-volume pair(s) are
          below the threshold but excluded.
        </div>
      ) : (
        <>
          {/* Every at-risk pair */}
          <div>
            <div className="flex justify-between items-center mb-1">
              <h3 className="text-lg font-semibold text-primary">Every At-Risk Merchant × Risk</h3>
              <Button
                size="sm"
                onClick={() => exportAtRiskToCSV(atRisk, `risks_needing_attention_${Date.now()}.csv`)}
                className="flex items-center gap-2"
              >
                <Download01 className="size-4" />
                Download At-Risk CSV
              </Button>
            </div>
            <p className="text-sm text-tertiary mb-4">
              Click a row to pull every flag behind it — explanation, confidence, reviewer feedback.
            </p>
            <TableCard.Root>
              <div className="overflow-x-auto">
                <Table
                  aria-label="At-risk merchant and risk pairs"
                  selectionMode="single"
                  selectionBehavior="replace"
                  selectedKeys={selectedPair ? [pairKey(selectedPair)] : []}
                  onSelectionChange={(keys) => {
                    if (keys === "all") return;
                    const key = keys.values().next().value;
                    setSelectedPairKey(key ? String(key) : null);
                  }}
                >
                  <Table.Header>
                    <Table.Head id="merchant" label="Merchant" isRowHeader />
                    <Table.Head id="risk" label="Risk" />
                    <Table.Head id="problem" label="Problem" />
                    <Table.Head id="total" label="Total" />
                    <Table.Head id="fp" label="FP" />
                    <Table.Head id="tp" label="TP" />
                    <Table.Head id="fn" label="FN" />
                    <Table.Head id="precision" label="Precision %" />
                    <Table.Head id="recall" label="Recall %" />
                    <Table.Head id="f1" label="F1 %" />
                  </Table.Header>
                  <Table.Body items={atRisk.map((row) => ({ ...row, id: pairKey(row) }))}>
                    {(row) => (
                      <Table.Row id={row.id} className="cursor-pointer">
                        <Table.Cell>{row.merchantId}</Table.Cell>
                        <Table.Cell>{row.risk}</Table.Cell>
                        <Table.Cell>
                          <ProblemCell problem={row.problem} />
                        </Table.Cell>
                        <Table.Cell>{row.total}</Table.Cell>
                        <Table.Cell>
                          <span className="font-semibold" style={{ color: RISK_PROBLEM_COLORS.falsePositives }}>
                            {row.fp}
                          </span>
                        </Table.Cell>
                        <Table.Cell>{row.tp}</Table.Cell>
                        <Table.Cell>
                          <span className="font-semibold" style={{ color: RISK_PROBLEM_COLORS.falseNegatives }}>
                            {row.fn}
                          </span>
                        </Table.Cell>
                        <Table.Cell>{row.precision.toFixed(1)}</Table.Cell>
                        <Table.Cell>{row.recall.toFixed(1)}</Table.Cell>
                        <Table.Cell>
                          <F1Cell f1={row.f1} />
                        </Table.Cell>
                      </Table.Row>
                    )}
                  </Table.Body>
                </Table>
              </div>
            </TableCard.Root>
          </div>

          {/* Flag-level drill-down for the selected pair */}
          {!selectedPair ? (
            <div className="text-center py-4 text-tertiary">Select a row above to see and download its flags.</div>
          ) : (
            <div>
              <div className="flex justify-between items-center mb-1">
                <h3 className="text-lg font-semibold text-primary">
                  Flags for {selectedPair.merchantId} × {selectedPair.risk}
                </h3>
                <Button
                  size="sm"
                  onClick={() =>
                    exportFlagDetailToCSV(
                      flagDetail,
                      `flags_${slugify(selectedPair.merchantId)}__${slugify(selectedPair.risk)}.csv`,
                    )
                  }
                  className="flex items-center gap-2"
                >
                  <Download01 className="size-4" />
                  Download Filtered CSV
                </Button>
              </div>
              <p className="text-sm text-tertiary mb-3">
                Rows: {flagDetail.length} · reviewed table total {selectedPair.total} (TP {selectedPair.tp} / FP{" "}
                {selectedPair.fp} / FN {selectedPair.fn})
              </p>
              <div className="mb-4">
                <Checkbox
                  label="Include flags with no human review yet (not counted in the table above)"
                  isSelected={includeUnreviewed}
                  onChange={setIncludeUnreviewed}
                />
              </div>

              {flagDetail.length === 0 ? (
                <div className="text-center py-4 text-tertiary">
                  No flag rows matched this merchant × risk in the loaded window.
                </div>
              ) : (
                <TableCard.Root>
                  <div className="max-h-96 overflow-auto">
                    <Table aria-label="Flags for the selected merchant and risk pair">
                      <Table.Header>
                        <Table.Head id="videoId" label="Video ID" isRowHeader />
                        <Table.Head id="outcome" label="Outcome" />
                        <Table.Head id="originalFlag" label="Original Flag" />
                        <Table.Head id="newFlag" label="New Flag" />
                        <Table.Head id="confidence" label="Confidence" />
                        <Table.Head id="explanation" label="Explanation" />
                        <Table.Head id="feedbackReason" label="Feedback Reason" />
                        <Table.Head id="feedbackText" label="Feedback Text" />
                        <Table.Head id="createdAt" label="Created At" />
                      </Table.Header>
                      <Table.Body items={flagDetail}>
                        {(record) => (
                          <Table.Row id={record.id}>
                            <Table.Cell className="whitespace-nowrap font-mono text-xs">
                              {record.videoId.replace("VID-", "")}
                            </Table.Cell>
                            <Table.Cell>
                              <OutcomeBadge actionTaken={record.actionTaken} />
                            </Table.Cell>
                            <Table.Cell>{record.originalFlag}</Table.Cell>
                            <Table.Cell>{record.newFlag}</Table.Cell>
                            <Table.Cell>{record.confidenceIndex.toFixed(2)}</Table.Cell>
                            <Table.Cell>
                              <span className="block max-w-md truncate" title={record.explanation}>
                                {record.explanation}
                              </span>
                            </Table.Cell>
                            <Table.Cell>{record.feedbackReason}</Table.Cell>
                            <Table.Cell>
                              <span className="block max-w-xs truncate" title={record.feedbackText ?? undefined}>
                                {record.feedbackText}
                              </span>
                            </Table.Cell>
                            <Table.Cell className="whitespace-nowrap">
                              {new Date(record.flagCreatedAt).toLocaleDateString()}
                            </Table.Cell>
                          </Table.Row>
                        )}
                      </Table.Body>
                    </Table>
                  </div>
                </TableCard.Root>
              )}
            </div>
          )}
        </>
      )}

      {/* All pairs, including healthy */}
      <div>
        <Button size="sm" color="secondary" onClick={() => setShowAllPairs((v) => !v)}>
          {showAllPairs ? "Hide" : "Show"} all merchant × risk pairs ({full.length}), including healthy ones
        </Button>
        {showAllPairs && (
          <TableCard.Root className="mt-4">
            <div className="max-h-96 overflow-auto">
              <Table aria-label="All merchant and risk pairs">
                <Table.Header>
                  <Table.Head id="merchant" label="Merchant" isRowHeader />
                  <Table.Head id="risk" label="Risk" />
                  <Table.Head id="total" label="Total" />
                  <Table.Head id="fp" label="FP" />
                  <Table.Head id="tp" label="TP" />
                  <Table.Head id="fn" label="FN" />
                  <Table.Head id="precision" label="Precision %" />
                  <Table.Head id="recall" label="Recall %" />
                  <Table.Head id="f1" label="F1 %" />
                </Table.Header>
                <Table.Body items={full.map((row) => ({ ...row, id: pairKey(row) }))}>
                  {(row) => (
                    <Table.Row id={row.id}>
                      <Table.Cell>{row.merchantId}</Table.Cell>
                      <Table.Cell>{row.risk}</Table.Cell>
                      <Table.Cell>{row.total}</Table.Cell>
                      <Table.Cell>{row.fp}</Table.Cell>
                      <Table.Cell>{row.tp}</Table.Cell>
                      <Table.Cell>{row.fn}</Table.Cell>
                      <Table.Cell>{row.precision.toFixed(1)}</Table.Cell>
                      <Table.Cell>{row.recall.toFixed(1)}</Table.Cell>
                      <Table.Cell>
                        <F1Cell f1={row.f1} />
                      </Table.Cell>
                    </Table.Row>
                  )}
                </Table.Body>
              </Table>
            </div>
          </TableCard.Root>
        )}
      </div>
    </div>
  );
}
